import { CommonUtility } from '@utility/common';
import { APIPath } from '@utility/constants/api';
import { CacheUtility } from '@utility/cache';
import { BaseService } from './base';
import { CRUDService } from './crud';

class StorageProvider extends CRUDService {
  constructor() {
    super(APIPath.fulfillmentProvider);
  }

  getAllFPs() {
    return BaseService.get(`${APIPath.fulfillmentProviders}?limit=500`);
  }

  inventoryList(id, params) {
    const url = `${
      APIPath.scmProvider
    }/${id}/inventory?${CommonUtility.objectToParams(params)}`;
    return BaseService.get(url);
  }

  /** EN-4200: the V2 stock of one provider, paged by item. */
  inventoryV2(id, params) {
    const url = `${APIPath.fulfillmentProvider}/${encodeURIComponent(
      id,
    )}/inventory?${CommonUtility.objectToParams(params)}`;
    return BaseService.get(url);
  }

  wmsInventoryList(params) {
    const url = `${APIPath.wmsItems}?${CommonUtility.objectToParams(params)}`;
    return BaseService.get(url);
  }

  wmsComponentList(params) {
    const url = `${APIPath.wmsComponents}?${CommonUtility.objectToParams(
      params,
    )}`;
    return BaseService.get(url);
  }

  updateProviderType(id, data) {
    const url = `${APIPath.fulfillmentProvider}/${id}/integration`;
    return BaseService.put(url, data);
  }

  componentList(id, params) {
    const url = `${
      APIPath.storageProviderComponents
    }/${id}?${CommonUtility.objectToParams(params)}`;
    return BaseService.get(url);
  }

  updateComponent(id, data) {
    const url = `${APIPath.storageProviderComponents}/${id}`;
    return BaseService.put(url, data);
  }

  scmDataProviderItems(data) {
    return BaseService.put(APIPath.scmInbound, data);
  }

  scmDataProviderComponents(data) {
    return BaseService.put(APIPath.scmInboundComponents, data);
  }

  updateInventory(id, data) {
    const url = `${APIPath.scmProvider}/${id}/inventory`;
    return BaseService.put(url, data);
  }

  scm(params) {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(`${APIPath.scmProvider}?${query}`);
  }

  getHierarchy = regionId =>
    BaseService.get(
      `${APIPath.fulfillmentProviderHierarchy}?${CommonUtility.objectToParams({
        marketplace_region_id: regionId,
      })}`,
    );

  saveHierarchy = data =>
    BaseService.put(APIPath.fulfillmentProviderHierarchy, data);

  updateFPThreshold = (marketplaceRegionId, data) =>
    BaseService.put(`${APIPath.fpThreshold}/${marketplaceRegionId}`, data);

  providerTypes = () => BaseService.get(APIPath.fulfillmentProviderTypes);

  getReturnProcessingCenter = region =>
    BaseService.get(
      `${APIPath.fulfillmentProviderReturnProcessingCenter}/${region}`,
    );

  saveReturnProcessingCenter = (fpId, data) =>
    BaseService.put(
      `${APIPath.fulfillmentProviderReturnProcessingCenter}/${fpId}`,
      data,
    );

  removeReturnProcessingCenter = regionId =>
    BaseService.remove(
      `${APIPath.fulfillmentProviderReturnProcessingCenter}/${regionId}`,
    );

  getWarehouseHierarchy = regionId =>
    BaseService.get(
      `${
        APIPath.fulfillmentProviderWarehouseHierarchy
      }?${CommonUtility.objectToParams({ marketplaceRegionId: regionId })}`,
    );

  saveWarehouseHierarchy = data =>
    BaseService.put(APIPath.fulfillmentProviderWarehouseHierarchy, data);

  marketplaces(id) {
    return BaseService.get(`${APIPath.scmProvider}/${id}/marketplaces`);
  }

  getShippingTemplate(id) {
    return BaseService.get(`${APIPath.scmProvider}/${id}/shipping-template`);
  }

  updateShippingTemplate(id, data) {
    return BaseService.put(
      `${APIPath.scmProvider}/${id}/shipping-template`,
      data,
    );
  }

  shippingTemplateRegion() {
    return BaseService.get(APIPath.scmProviderShippingTemplateRegion);
  }

  getReport(id, params) {
    const query = CommonUtility.objectToParams(params);
    return BaseService.get(
      `scm/providers/${id}/inventory/invoke-lambda?${query}`,
    );
  }

  blankBoxFulfillmentSettings({ id, item_id, is_blank_box }) {
    return BaseService.put(`scm/providers/${id}/inventory-settings`, {
      item_id,
      is_blank_box,
    });
  }

  configureItemPrediction(data, providerId) {
    return BaseService.put(
      `${APIPath.fullfillmentProviderConfigurePrediction}/${providerId}`,
      data,
    );
  }

  syncInventory(data) {
    return BaseService.put(APIPath.fullfillmentProviderSyncInventory, data);
  }

  syncFulfillmentDeliverrInventory() {
    return BaseService.get(APIPath.fulfillmentRefreshDeliverrInventory);
  }

  cbm(id) {
    return BaseService.get(`${APIPath.scmProvider}/${id}/inventory/cbm`);
  }

  updateItems(id, payload) {
    return BaseService.put(`${APIPath.scmProvider}/${id}/inventory`, payload);
  }

  updateWindowVelocity(id, payload) {
    return BaseService.put(`${APIPath.scmWindow}/${id}`, payload);
  }

  authFulfillmentTactical(formData) {
    return BaseService.post(`${APIPath.authFulfillmentTactical}`, formData);
  }

  authMintsoft(formData) {
    return BaseService.post(`${APIPath.authMintsoft}`, formData);
  }

  authSkudrop(formData) {
    return BaseService.post(`${APIPath.authSkudrop}`, formData);
  }

  authShipHero(token, payload) {
    const encoded = encodeURIComponent(token);
    return BaseService.post(`${APIPath.authShipHero}/${encoded}`, payload);
  }

  authSantFe(formData) {
    return BaseService.post(`${APIPath.authSantaFe}`, formData);
  }

  authCamelot(formData) {
    return BaseService.post(`${APIPath.authCamelot}`, formData);
  }

  authGoodCang(formData) {
    return BaseService.post(`${APIPath.authGoodCang}`, formData);
  }

  authFulfillmentDeliverr() {
    return BaseService.get(APIPath.authFulfillmentDeliverr);
  }

  authFulfillmentDeliverrCode(code) {
    const encoded = encodeURIComponent(code);
    return BaseService.post(
      `${APIPath.authFulfillmentDeliverrCode}/${encoded}`,
    );
  }

  getShipments(id) {
    return BaseService.get(`${APIPath.timelineFulfillmentProvider}/${id}`);
  }

  fetchUnmatchedSkuList(id = '', params) {
    const encoded = CommonUtility.objectToParams(params);
    return BaseService.get(
      `${APIPath.scmProvider}/${id}/unmatched-sku-list?${encoded}`,
    );
  }

  fetchSetSku(id = '', data) {
    return BaseService.put(`${APIPath.scmProvider}/${id}/set-sku`, data);
  }

  fetchRemoveSku(id = '', alias = '') {
    const encoded = encodeURIComponent(alias);
    return BaseService.remove(
      `${APIPath.scmProvider}/${id}/remove-sku/${encoded}`,
    );
  }

  fetchGetFpDefaultPredictions = marketplaceRegionId =>
    BaseService.get(`${APIPath.fpPredictionsDefault}/${marketplaceRegionId}`);

  fetchFPThreshold = marketplaceRegionId =>
    BaseService.get(`${APIPath.fpThreshold}/${marketplaceRegionId}`);

  fetchUpdateFpDefaultPredictions = (marketplaceRegionId, data) =>
    BaseService.put(
      `${APIPath.fpPredictionsDefault}/${marketplaceRegionId}`,
      data,
    );

  updateRegionManualMode = (marketplaceRegionId, data) =>
    BaseService.put(
      `${APIPath.fpFulfillmentSetting}/${marketplaceRegionId}`,
      data,
    );

  // SnapShot Dates
  fetchInventoryValuationSnapshotDates = () =>
    BaseService.get(APIPath.inventoryValuationSnapshotDates);

  // SnapShot CSV Data
  fetchInventoryValuationSnapshot = date => {
    const encoded = encodeURIComponent(date);
    return BaseService.get(
      `${APIPath.inventoryValuationSnapshot}?date=${encoded}`,
    );
  };

  syncExtensiv = () => BaseService.get(APIPath.syncExtensiv);

  syncMintsoft = () => BaseService.get(APIPath.syncMintsoft);

  updateQuantityType = (id, payload) =>
    BaseService.put(
      `${APIPath.fulfillmentProvider}/${id}/quantityType`,
      payload,
    );

  updateType = (id, data) => {
    const url = `${APIPath.fulfillmentProvider}/${id}/type`;
    return BaseService.put(url, data);
  };

  // Fetch Timeline Prediction Graph
  fetchScmPredictionGraph = async (upc, forceRefresh = false) => {
    const url = `${APIPath.scmPredictionGraph}/${upc}`;
    const cache = CacheUtility.get(url);
    if (cache && !forceRefresh) {
      return cache;
    }
    const res = await BaseService.get(url);
    CacheUtility.save(url, res);
    return res;
  };

  tacticalAuth = data => BaseService.put(APIPath.authFulfillmentTactical, data);

  providerRate = (id, data) =>
    BaseService.post(`${APIPath.fulfillmentProvider}/${id}/rate`, data);

  wmsProviderRate = id =>
    BaseService.get(`${APIPath.fulfillmentProviderWMS}/${id}/rates`);

  wmsProviderMaterial = id =>
    BaseService.get(
      `${APIPath.fulfillmentProviderWMS}/${id}/packaging-materials`,
    );

  wmsProviderInvoices = (id, params) =>
    BaseService.get(
      `${
        APIPath.fulfillmentProviderWMS
      }/${id}/invoices/?${CommonUtility.objectToParams(params)}`,
    );

  wmsProviderInvoiceDetail = (providerId, id) =>
    BaseService.get(
      `${APIPath.fulfillmentProviderWMS}/${providerId}/invoices/${id}`,
    );

  getWMSInvoicesLogs = (providerId, id, params) =>
    BaseService.get(
      `${
        APIPath.fulfillmentProviderWMS
      }/${providerId}/invoices/${id}/logs/?${CommonUtility.objectToParams(
        params,
      )}`,
    );

  exportWMSInvoicesLogs = id =>
    BaseService.get(`${APIPath.fulfillmentProviderWMSInvoice}/${id}/csv`);

  getInvoicePDF(providerId, invoiceID, config, header) {
    return BaseService.get(
      `${APIPath.fulfillmentProviderWMS}/${providerId}/invoices/${invoiceID}/pdf`,
      false,
      true,
      // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'CancelToken | un...
      null,
      config,
      header,
    );
  }

  getInventoryExportHistory(page, limit) {
    return BaseService.get(
      `${APIPath.fpInventoryExport}?page=${page}&limit=${limit}`,
    );
  }

  createInventoryExport(payload) {
    return BaseService.post(APIPath.fpInventoryExport, payload);
  }
}

const StorageProviderService = new StorageProvider();
Object.freeze(StorageProviderService);
export default StorageProviderService;
